import React, { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { AgGridReact } from '@ag-grid-community/react';
import { Button, Form, Grid, Segment } from 'semantic-ui-react';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { TaskFormField, styleObj, marginBottom } from './lib/constants';
import { STATUS_LIST } from '../../../../HelperMethods/enums';
import * as DateUtils from '../../../../HelperMethods/DateUtils';
import LinkedTaskList from './LinkedTaskList/LinkedTaskList';
import linkTaskIcon from '../../../../icons/link-task-button.png';
import {
  ACCESS_OPTIONS,
  ENTITY_ACCESS_TYPE,
  RESOURCE_TYPE,
  RESOURCES,
} from '../../../../HelperMethods/role-resource-constants';
import { determineIfUserHasFieldsDisabled } from '../../../../HelperMethods/UserUtilities';
import ShouldComponentRender from '../../../common/ShouldComponentRender';
import AgGridContainer from '../../../common/AgGridContainer';
import ActionsColumnRenderer from '../../../common/ActionsColumnRenderer';
import { workFlowGridTemplateColumns } from '../../../../HelperMethods/workflowUtils';
import { GRID_OPTIONS } from '../../../../HelperMethods/AgGridConstants';
import TaskFormMap from './TaskFormMap';
import { shouldMapBeShown } from './lib/Mapper';
import { isFormAttributeHiddenOrInvalid } from '../../../../HelperMethods/FormUtils';
import FormPreview from '../../../common/form-preview/FormPreviewComponent';
import ManifestFormAuditLog from './ManifestFormAuditLog/ManifestFormAuditLog';
import { joyn2UserPropType } from '../../../../common/prop-types';
import GenerateMultipleModal from './GenerateMultipleModal';
import { isTaskParent, getUrlQueryParams } from '../../../../HelperMethods/TaskUtilities';
import TaskAttachmentSegment from './TaskAttachmentSegment';
import InstructionsSegment from './InstructionsSegment';
import { ATTACHMENT_SCHEMA } from './lib/schemaConstants';
import ParentTaskTemplateSegment from './ParentTaskTemplateSegment';
import { overlayCellRenderer } from '../helper';
import CustomTooltip from './CustomTooltip';
import ReadingsComponent from './ReadingsComponent';
import { sleep } from '../../../../HelperMethods/UtilityFunctions';
import TasksFromRulesList from './TasksFromRulesList';
import { actionRecordUtils } from '../../../../HelperMethods/LocalStorageUtils';

const sortFunctionForAttributesInSchema = (attributeObj1, attributeObj2) => {
  const seq1 = !_.isNil(attributeObj1.sequence) ? attributeObj1.sequence : 1000;
  const seq2 = !_.isNil(attributeObj2.sequence) ? attributeObj2.sequence : 1000;
  const seqDiff = seq1 - seq2;
  if (seqDiff === 0) {
    return (attributeObj1.displayName || attributeObj1.name).localeCompare(
      attributeObj2.displayName || attributeObj2.name
    );
  }
  return seq1 - seq2;
};

const shouldShowApprovalMatrixInfo = (task) => {
  return task.id && task.approvalInfo;
};

function mapSchemaToSystemAndCustom(schema) {
  const customSchema = [];
  const systemSchema = [];
  const scheduleSchema = [];
  const auditSchema = [];
  const notificationSchema = [];
  const partsSchema = [];
  const globalSchema = [];
  const approvalSchema = [];
  const watchergroupSchema = [];
  let attachmentSchema;
  for (const [attributeName, attributeObj] of Object.entries(schema)) {
    if (typeof attributeObj !== 'object') continue;
    const newAttributeObj = _.clone(attributeObj);
    newAttributeObj.attributeName = attributeName;
    if (attributeName.startsWith('s_')) {
      systemSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('c_')) {
      customSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('g_')) {
      globalSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('schedule_')) {
      scheduleSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('audit_')) {
      auditSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('notification_')) {
      notificationSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('parts_')) {
      partsSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('watcher_')) {
      watchergroupSchema.push(newAttributeObj);
    }
    if (attributeName.startsWith('approval_')) {
      approvalSchema.push(newAttributeObj);
    }
    if (attributeName === ATTACHMENT_SCHEMA) {
      attachmentSchema = newAttributeObj;
    }
  }
  systemSchema.sort(sortFunctionForAttributesInSchema);
  customSchema.sort(sortFunctionForAttributesInSchema);
  scheduleSchema.sort(sortFunctionForAttributesInSchema);
  auditSchema.sort(sortFunctionForAttributesInSchema);
  approvalSchema.sort(sortFunctionForAttributesInSchema);
  globalSchema.sort(sortFunctionForAttributesInSchema);
  return {
    customSchema,
    systemSchema,
    scheduleSchema,
    auditSchema,
    notificationSchema,
    partsSchema,
    globalSchema,
    approvalSchema,
    attachmentSchema,
    watchergroupSchema,
  };
}

function shouldShowLinkedTaskSegment(task, linkedTasks) {
  return (
    task.id && (linkedTasks.items.length > 0 || linkedTasks.isLoading || (task.taskType && task.taskType.isLinkable))
  );
}

function shouldShowTaskTemplateSegment(task, templateInfo) {
  return (
    task.id && (templateInfo.items.length > 0 || templateInfo.isLoading || (task.template && task.template.length > 0))
  );
}

function shouldShowReadingsData(task) {
  if (task?.taskType?.rt_id && task?.asset?.type !== 'stop') {
    return true;
  }
  return false;
}

function shouldShowParentTaskTemplateSegment(parentTaskTemplate, taskType) {
  return taskType?.isDynamicParentChild || parentTaskTemplate.items.length > 0 || parentTaskTemplate.isLoading;
}
function selectedTasktype(formik, taskTypeOptions) {
  const taskTypeId = _.get(formik, 'values.s_tasktype', '');
  if (!taskTypeId) return false;

  return taskTypeOptions.items.find((tT) => tT.key === taskTypeId);
}

function getSelectedLocation(formik, assetOptions, stopOptions) {
  const locationId = _.get(formik, 'values.s_asset') || _.get(formik, 'values.s_stop');
  if (!locationId) return null;
  return [..._.get(assetOptions, 'items', []), ..._.get(stopOptions, 'items', [])].find((loc) => loc.key === locationId)
    ?.originalobject;
}

function shouldShowPartsSchema(formik, taskTypeOptions) {
  const taskType = selectedTasktype(formik, taskTypeOptions);
  const partsPicklist = _.get(taskType, 'originalobject.partsPicklist');
  return partsPicklist && partsPicklist.length > 0;
}

async function createOrUpdateTask(createOrUpdateFunc, urlParams, formik) {
  try {
    const { location, history } = urlParams;
    const queryParams = getUrlQueryParams(location);
    const res = await createOrUpdateFunc(formik.values, formik);
    if (res) {
      if (res.donotRedirect) {
        return;
      }
      if (queryParams.parentLinkedTaskId) {
        history.goBack();
      } else {
        history.push('/taskViewPage');
      }
    }
  } catch (error) {
    console.error(error);
  }
}

const TaskForm = React.forwardRef(
  (
    {
      task,
      onApprove,
      onCancel,
      onDiscard,
      initialValues,
      validateForm,
      schema,
      dependentTasks,
      linkedTasks,
      templateInfo,
      parentTaskTemplate,
      onLinkTaskButtonClicked,
      editLinkedTask,
      onDeleteButtonClicked,
      notificationWatchers,
      partsPicklist,
      manifestFormPreview,
      allAreas,
      taskHistory,
      taskEmailHistory,
      watchergroups,
      taskTypeSelectedisParent,
      showEquipmentColumnForParentTemplate,
      allTasks,
      ...props
    },
    ref
  ) => {
    const {
      systemSchema,
      customSchema,
      scheduleSchema,
      auditSchema,
      notificationSchema,
      partsSchema,
      globalSchema,
      approvalSchema,
      attachmentSchema,
      watchergroupSchema,
      // map the systemSchema here
    } = mapSchemaToSystemAndCustom(schema);
    const { manifestForm, taskForm } = ref;
    const {
      onCreateButtonClicked,
      onUpdate,
      buttonDisableState,
      onResolveAndUpdateButtonClicked,
      vendorsDict,
      evaluatedRules,
    } = props;

    const location = useLocation();
    const history = useHistory();
    const { schema: manifestFormSchema, data, picklist, isLoading: loadingManifestForm } = manifestFormPreview;
    const generateMultiple = _.get(task, 'taskType.generateMultiple', false);
    const [open, handleModal] = useState(false);
    const [styleObjforCreateUpdateButton, setstyleObjforCreateUpdateButton] = useState({});
    const [marginbottom, setmarginBottom] = useState('');
    const parentTask = task.parentTaskId ? (allTasks || []).find((t) => t.id === task.parentTaskId) : null;
    const searchPicklistForForm = useSelector((state) => state.manageTaskFormReducer.searchPicklistForForm, _.isEqual);

    const isActionRecordPresent = useMemo(() => {
      return actionRecordUtils.GET_ACTION_RECORD();
    }, []);

    React.useEffect(() => {
      const style = styleObj(history);
      if (props.ifSmallScreen) {
        delete style.alignItems;
        delete style.justifyContent;
        delete style.width;
        style.padding = '10px';
      }
      setstyleObjforCreateUpdateButton(style);
      setmarginBottom(marginBottom(history));
    }, []);
    // console.log('2. Form renderer systemSchema: ', systemSchema)
    return (
      <>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validate={validateForm}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {(formik) => {
            return (
              <form
                style={{
                  height: '100%',
                  overflowY: 'auto',
                  paddingLeft: '14px',
                  paddingRight: '14px',
                }}
                className="ui form"
                ref={taskForm}
                autoComplete="off"
              >
                <div style={{ marginBottom: marginbottom }}>
                  <Segment style={{ paddingBottom: '36px' }} id="systemSegment">
                    <Grid>
                      <Grid.Row style={{ margin: '12px'}}>
                        {systemSchema.map((attributeObj) => (
                          <TaskFormField
                            attributeObj={attributeObj}
                            key={attributeObj.attributeName}
                            formik={formik}
                            task={task}
                            taskTypeSelectedisParent={taskTypeSelectedisParent}
                            taskType={_.get(selectedTasktype(formik, props.taskTypeOptions), 'originalobject')}
                            ifSmallScreen={props.ifSmallScreen}
                            {...props}
                          />
                        ))}
                      </Grid.Row>
                    </Grid>
                  </Segment>

                  {shouldShowReadingsData(task) && (
                    <ReadingsComponent assetId={task?.asset?.id} taskId={task?.readingId || task?.id} />
                  )}

                  {shouldShowParentTaskTemplateSegment(
                    parentTaskTemplate,
                    selectedTasktype(formik, props.taskTypeOptions)?.originalobject
                  ) && (
                    <ParentTaskTemplateSegment
                      taskType={selectedTasktype(formik, props.taskTypeOptions).originalobject}
                      parentTaskTemplate={parentTaskTemplate}
                      task={task}
                      onParentTempalteSearchChange={props.onParentTempalteSearchChange}
                      onParentTempalteItemChange={props.onParentTempalteItemChange}
                      onTemplateItemTaskTypeChanged={props.onTemplateItemTaskTypeChanged}
                      formik={formik}
                      showEquipmentColumnForParentTemplate={showEquipmentColumnForParentTemplate}
                    />
                  )}
                  {shouldShowApprovalMatrixInfo(task) ? (
                    <Segment style={{ border: '1px solid #e5e5e5', marginTop: '10px' }}>
                      <Grid>
                        <Grid.Row className="form-group-header">
                          <Grid.Column width={12}>Approval Requirement</Grid.Column>
                          <Grid.Column width={4}>
                            <div id="portal-button" />
                          </Grid.Column>
                        </Grid.Row>

                        <Grid.Row style={{ marginLeft: '5px' }}>
                          <Grid.Column width={16}>
                            <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
                              <Grid.Row>
                                {approvalSchema.map((attributeObj) => {
                                  return (
                                    <TaskFormField
                                      attributeObj={attributeObj}
                                      key={attributeObj.attributeName}
                                      formik={formik}
                                      task={task}
                                      requesterOptions={notificationWatchers}
                                      {...props}
                                      width="12"
                                    />
                                  );
                                })}
                              </Grid.Row>
                            </Grid>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  ) : null}

                  <Segment style={{ border: '1px solid #e5e5e5', marginTop: '10px' }} id="scheduleSegment">
                    <Grid>
                      <Grid.Row className="form-group-header">Scheduling Details</Grid.Row>

                      <Grid.Row style={{ marginLeft: '5px' }}>
                        <Grid.Column width={16}>
                          <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
                            <Grid.Row>
                              {scheduleSchema.map((attributeObj) => {
                                return (
                                  <TaskFormField
                                    attributeObj={attributeObj}
                                    key={attributeObj.attributeName}
                                    formik={formik}
                                    task={task}
                                    taskTypeSelectedisParent={taskTypeSelectedisParent}
                                    {...props}
                                  />
                                );
                              })}
                            </Grid.Row>
                          </Grid>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>

                  {shouldShowPartsSchema(formik, props.taskTypeOptions) && (
                    <Segment style={{ border: '1px solid #e5e5e5', marginTop: '10px' }} id="partsSegment">
                      <Grid>
                        <Grid.Row className="form-group-header">Parts Required</Grid.Row>
                        <Grid.Row style={{ marginLeft: '5px' }}>
                          <Grid.Column width={16}>
                            <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
                              <Grid.Row>
                                {partsSchema.map((attributeObj) => {
                                  return (
                                    <TaskFormField
                                      attributeObj={attributeObj}
                                      key={attributeObj.attributeName}
                                      formik={formik}
                                      task={task}
                                      partsPicklist={partsPicklist}
                                      {...props}
                                      width="12"
                                    />
                                  );
                                })}
                              </Grid.Row>
                            </Grid>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  )}
                  {manifestFormSchema && Object.keys(manifestFormSchema).length ? (
                    <Segment style={{ border: '1px solid #e5e5e5', marginTop: '10px' }} id="manifestFormSegment">
                      <Grid>
                        <span style={{ paddingLeft: '20px', paddingTop: '12px' }}>{manifestFormSchema.name}</span>
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <Grid style={{ margin: '0 5px' }}>
                              <Grid.Row>
                                <FormPreview
                                  width="85%"
                                  ref={manifestForm}
                                  task={task}
                                  formData={data}
                                  originalSchema={manifestFormSchema}
                                  loggedInUser={props.loggedInUser}
                                  picklistDict={_.keyBy(picklist, 'id')}
                                  selectedTasktype={selectedTasktype(formik, props.taskTypeOptions)}
                                  taskformValues={formik.values}
                                  taskformTouch={formik.touched}
                                  taskHistory={taskHistory}
                                  parentTask={parentTask}
                                  searchPicklistForForm={searchPicklistForForm}
                                  showQr
                                  {...props}
                                />
                              </Grid.Row>
                            </Grid>
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <ManifestFormAuditLog data={manifestFormPreview.auditLog} />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  ) : null}
                  {globalSchema.length > 0 && (
                    <Segment style={{ border: '1px solid #e5e5e5', marginTop: '10px' }} id="globalSegment">
                      <Grid>
                        <Grid.Row className="form-group-header">Global Task Attributes</Grid.Row>
                        <Grid.Row style={{ marginLeft: '5px' }}>
                          <Grid.Column width={16}>
                            <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
                              <Grid.Row>
                                {globalSchema.map((attributeObj) => {
                                  return (
                                    <TaskFormField
                                      key={attributeObj.attributeName}
                                      attributeObj={attributeObj}
                                      formik={formik}
                                      task={task}
                                      {...props}
                                    />
                                  );
                                })}
                              </Grid.Row>
                            </Grid>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  )}
                  {customSchema.some((attributeObj) => {
                    return !isFormAttributeHiddenOrInvalid(attributeObj, {
                      formValues: formik.values,
                      task,
                    });
                  }) && (
                    <Segment id="customSegment">
                      <Grid>
                        <Grid.Row className="form-group-header">Task Type Attributes</Grid.Row>
                        <Grid.Row style={{ marginLeft: '5px' }}>
                          <Grid.Column width={16}>
                            <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
                              <Grid.Row>
                                {customSchema.map((attributeObj) => {
                                  return (
                                    <TaskFormField
                                      key={attributeObj.attributeName}
                                      attributeObj={attributeObj}
                                      formik={formik}
                                      task={task}
                                      {...props}
                                    />
                                  );
                                })}
                              </Grid.Row>
                            </Grid>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  )}

                  <TaskAttachmentSegment
                    taskType={selectedTasktype(formik, props.taskTypeOptions)}
                    attachmentSchema={attachmentSchema}
                    location={getSelectedLocation(formik, props.assetOptions, props.stopOptions)}
                    formik={formik}
                    task={task}
                  />
                  <InstructionsSegment taskType={selectedTasktype(formik, props.taskTypeOptions)} />

                  {shouldShowLinkedTaskSegment(task, linkedTasks) && (
                    <Segment>
                      <Grid>
                        <Grid.Row className="form-group-header">Linked Tasks</Grid.Row>
                        {linkedTasks.isLoading ? (
                          <div className="hover-loading">
                            <div className="joyn2-loader" />
                          </div>
                        ) : (
                          <>
                            {task.taskType && task.taskType.isLinkable && (
                              <Grid.Row>
                                <Grid.Column width={5} floated="right" textAlign="right">
                                  <Button type="button" secondary onClick={onLinkTaskButtonClicked}>
                                    <img alt="link-task" style={{ height: '28px' }} src={linkTaskIcon} />
                                  </Button>
                                </Grid.Column>
                              </Grid.Row>
                            )}
                            {linkedTasks.items.length > 0 && (
                              <Grid.Row>
                                <Grid.Column width={16}>
                                  <LinkedTaskList
                                    taskList={linkedTasks.items}
                                    onEditButtonClicked={editLinkedTask}
                                    onDeleteButtonClicked={onDeleteButtonClicked}
                                    allAreas={allAreas}
                                    vendorsDict={vendorsDict}
                                    notificationWatchers={notificationWatchers}
                                    assetLevelsDict={props?.assetLevelsDict ?? {}}
                                  />
                                </Grid.Column>
                              </Grid.Row>
                            )}
                          </>
                        )}
                      </Grid>
                    </Segment>
                  )}
                  {shouldShowTaskTemplateSegment(task, templateInfo) && (
                    <Segment>
                      <Grid>
                        <Grid.Row className="form-group-header">Template</Grid.Row>
                        {templateInfo.isLoading ? (
                          <div className="hover-loading">
                            <div className="joyn2-loader" />
                          </div>
                        ) : (
                          <>
                            {templateInfo.items.length > 0 && (
                              <Grid.Row>
                                <Grid.Column width={16}>
                                  <AgGridContainer numberOfRow={templateInfo.items.length || 0}>
                                    <div
                                      className="ag-theme-balham"
                                      style={{
                                        height: '100%',
                                        width: '100%',
                                      }}
                                    >
                                      <AgGridReact
                                        {...GRID_OPTIONS}
                                        columnDefs={workFlowGridTemplateColumns}
                                        onRowDataChanged={(params) => {
                                          params.columnApi.autoSizeAllColumns();
                                        }}
                                        frameworkComponents={{
                                          actionsColumnRenderer: ActionsColumnRenderer,
                                        }}
                                        context={{}}
                                        onGridReady={(params) => {
                                          params.api.sizeColumnsToFit();
                                        }}
                                        rowData={templateInfo.items}
                                      />
                                    </div>
                                  </AgGridContainer>
                                </Grid.Column>
                              </Grid.Row>
                            )}
                          </>
                        )}
                      </Grid>
                    </Segment>
                  )}

                  {(dependentTasks.items.length > 0 || dependentTasks.isLoading) && (
                    <Segment style={{ paddingBottom: '36px' }}>
                      <Grid>
                        <Grid.Row className="form-group-header">Dependent Tasks</Grid.Row>
                        {dependentTasks.isLoading ? (
                          <div className="hover-loading">
                            <div className="joyn2-loader" />
                          </div>
                        ) : (
                          <>
                            {dependentTasks.items.map((val) => (
                              <>
                                <Grid.Row style={{ paddingLeft: '15px', paddingTop: '15px' }}>{val.taskType}</Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={6}>
                                    <Form.Field>
                                      <label>Asset</label>
                                      <input disabled value={val.asset} />
                                    </Form.Field>
                                  </Grid.Column>
                                  <Grid.Column width={6}>
                                    <Form.Field>
                                      <label>Assigned To</label>
                                      <input disabled value={val.assignee} />
                                    </Form.Field>
                                  </Grid.Column>
                                </Grid.Row>
                              </>
                            ))}
                          </>
                        )}
                      </Grid>
                    </Segment>
                  )}
                  {auditSchema.some((attributeObj) => {
                    return !isFormAttributeHiddenOrInvalid(attributeObj, {
                      formValues: formik.values,
                      task,
                    });
                  }) && (
                    <Segment>
                      <Grid>
                        <Grid.Row className="form-group-header">Audit History</Grid.Row>
                        <Grid.Row>
                          <Grid.Column>
                            <AgGridContainer numberOfRow={taskHistory?.length}>
                              <div
                                className="ag-theme-balham"
                                style={{
                                  height: '100%',
                                  width: '100%',
                                }}
                              >
                                <AgGridReact
                                  {...GRID_OPTIONS}
                                  columnDefs={[
                                    { headerName: 'Date', field: 'date', rowGroup: true },

                                    { headerName: 'Create or Update', field: 'createOrUpdate' },
                                    { headerName: 'Section', field: 'section' },
                                    { headerName: 'Attribute', field: 'attribute' },
                                    {
                                      headerName: 'Atrribute New Value/Default Value',
                                      field: 'taskAfter',
                                    },
                                    {
                                      headerName: 'Attribute Old Value',
                                      field: 'taskBefore',
                                    },
                                    { headerName: 'User', field: 'user' },
                                  ]}
                                  rowData={taskHistory}
                                  rowSelection="multiple"
                                  rowMultiSelectWithClick="true"
                                  statusBar={{
                                    statusPanels: [
                                      {
                                        statusPanel: 'agTotalAndFilteredRowCountComponent',
                                        align: 'left',
                                      },
                                    ],
                                  }}
                                />
                              </div>
                            </AgGridContainer>
                          </Grid.Column>
                        </Grid.Row>
                        {/* {shouldMapBeShown(formik) && (
                        <Grid.Row>
                          <div className="form-map">
                            <TaskFormMap task={task} />
                          </div>
                        </Grid.Row>
                      )} */}
                      </Grid>
                    </Segment>
                  )}
                  {auditSchema.some((attributeObj) => {
                    return !isFormAttributeHiddenOrInvalid(attributeObj, {
                      formValues: formik.values,
                      task,
                    });
                  }) && (
                    <Segment>
                      <Grid>
                        <Grid.Row className="form-group-header">Audit Details</Grid.Row>
                        <Grid.Row>
                          {auditSchema.map((attributeObj) => {
                            return (
                              <TaskFormField
                                attributeObj={attributeObj}
                                key={attributeObj.attributeName}
                                formik={formik}
                                task={task}
                                {...props}
                              />
                            );
                          })}
                        </Grid.Row>
                        {shouldMapBeShown(formik) && (
                          <Grid.Row>
                            <div className="form-map">
                              <TaskFormMap task={task} />
                            </div>
                          </Grid.Row>
                        )}
                      </Grid>
                    </Segment>
                  )}
                  {task?.id && (
                    <Segment>
                      <Grid>
                        <Grid.Row className="form-group-header">Task Email History</Grid.Row>
                        <Grid.Row
                          style={{
                            minHeight: '250px',
                          }}
                        >
                          <Grid.Column>
                            <AgGridContainer numberOfRow={taskEmailHistory?.length}>
                              <div
                                className="ag-theme-balham"
                                style={{
                                  height: '100%',
                                  width: '100%',
                                  minHeight: '238px',
                                }}
                              >
                                <AgGridReact
                                  {...GRID_OPTIONS}
                                  columnDefs={[
                                    {
                                      headerName: 'Date',
                                      field: 'createdOn',
                                      width: 200,
                                      valueFormatter: (x) =>
                                        x.value ? DateUtils.convertUTCDateStringToYYYYMMDDHHMMSSFormat(x.value) : '',
                                    },
                                    {
                                      headerName: 'Action Types',
                                      field: 'actionTypes',
                                      valueGetter: (params) =>
                                        (_.get(params, 'data.actionTypes') ?? []).map((a) => a).join(', '),
                                      width: 300,
                                    },
                                    {
                                      headerName: 'Recipient Emails',
                                      field: 'emailAddressList',
                                      valueGetter: (params) =>
                                        (_.get(params, 'data.emailAddressList') ?? []).map((a) => a).join(', '),
                                      width: 720,
                                      cellRenderer: overlayCellRenderer,
                                      tooltipField: 'emailAddressList',
                                      tooltipComponent: 'customTooltip',
                                    },
                                  ]}
                                  rowData={taskEmailHistory}
                                  rowSelection="multiple"
                                  rowMultiSelectWithClick="true"
                                  statusBar={{
                                    statusPanels: [
                                      {
                                        statusPanel: 'agTotalAndFilteredRowCountComponent',
                                        align: 'left',
                                      },
                                    ],
                                  }}
                                  frameworkComponents={{
                                    customTooltip: CustomTooltip,
                                  }}
                                />
                              </div>
                            </AgGridContainer>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  )}
                  {task?.id && !!evaluatedRules?.length && (
                    <Segment>
                      <Grid>
                        <Grid.Row className="form-group-header">
                          Tasks Created/API Triggered From Rule Evaluation
                        </Grid.Row>
                        <Grid.Row
                          style={{
                            minHeight: '250px',
                          }}
                        >
                          <Grid.Column>
                            <AgGridContainer numberOfRow={evaluatedRules?.length}>
                              <TasksFromRulesList rulesEvaluated={evaluatedRules ?? []} />
                            </AgGridContainer>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  )}

                  <Segment style={{ border: '1px solid #e5e5e5', marginTop: '10px' }}>
                    <Grid>
                      <Grid.Row className="form-group-header">Watchers</Grid.Row>

                      <Grid.Row style={{ marginLeft: '5px' }}>
                        <Grid.Column width={16}>
                          <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
                            <Grid.Row>
                              {notificationSchema.map((attributeObj) => {
                                return (
                                  <TaskFormField
                                    attributeObj={attributeObj}
                                    key={attributeObj.attributeName}
                                    formik={formik}
                                    task={task}
                                    notificationWatchers={notificationWatchers}
                                    {...props}
                                  />
                                );
                              })}
                            </Grid.Row>
                          </Grid>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>

                  <Segment style={{ border: '1px solid #e5e5e5', marginTop: '10px' }} id="watchergroupSegment">
                    <Grid>
                      <Grid.Row className="form-group-header">Watchergroups</Grid.Row>

                      <Grid.Row style={{ marginLeft: '5px' }}>
                        <Grid.Column width={16}>
                          <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
                            <Grid.Row>
                              {watchergroupSchema.map((attributeObj) => {
                                return (
                                  <TaskFormField
                                    attributeObj={attributeObj}
                                    key={attributeObj.attributeName}
                                    formik={formik}
                                    task={task}
                                    watcherGroups={watchergroups.map((val) => {
                                      return {
                                        key: val.id,
                                        value: val.id,
                                        text: val.name,
                                      };
                                    })}
                                    {...props}
                                  />
                                );
                              })}
                            </Grid.Row>
                          </Grid>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                </div>
                <div>
                  <Grid>
                    <Grid.Row
                      className="form-button-row"
                      style={{
                        margin: '0px',
                        padding: '0px',
                        display: 'flex',
                        alignItems: 'center ',
                      }}
                    >
                      <Grid.Column className="fixed-button-wrapper" style={{ ...styleObjforCreateUpdateButton }}>
                        {!task.id && (
                          <Button
                            primary
                            type="button"
                            onClick={() => {
                              createOrUpdateTask(onCreateButtonClicked, { location, history }, formik);
                            }}
                            disabled={buttonDisableState}
                          >
                            Create
                          </Button>
                        )}

                        {/* {task.id && task.status === STATUS_LIST.DRAFT.key && (
                        <ShouldComponentRender
                          resource={RESOURCES.ENTITY.TASK}
                          resourceType={RESOURCE_TYPE.ENTITY}
                          entityAccessType={ENTITY_ACCESS_TYPE.UPDATE}
                          entity={task}
                        >
                          <Button
                            primary
                            type="button"
                            onClick={() => {
                              onApprove(formik.values, formik);
                            }}
                            disabled={determineIfUserHasFieldsDisabled({
                              resource: RESOURCES.ENTITY.TASK,
                              entityAccessType: ENTITY_ACCESS_TYPE.UPDATE,
                              user: props.loggedInUser,
                              field: ACCESS_OPTIONS.DISABLED_FIELD.options.STATUS,
                            })}
                          >
                            Confirm
                          </Button>
                        </ShouldComponentRender>
                      )} */}

                        {task.id && (
                          <ShouldComponentRender
                            resource={RESOURCES.ENTITY.TASK}
                            resourceType={RESOURCE_TYPE.ENTITY}
                            entityAccessType={ENTITY_ACCESS_TYPE.UPDATE}
                            entity={task}
                          >
                            {task.id &&
                              _.get(formik, 'values.s_status') !== STATUS_LIST.RESOLVED.key &&
                              task?.status !== STATUS_LIST.RESOLVED.key &&
                              !determineIfUserHasFieldsDisabled({
                                resource: RESOURCES.ENTITY.TASK,
                                entityAccessType: ENTITY_ACCESS_TYPE.UPDATE,
                                user: props.loggedInUser,
                                field: ACCESS_OPTIONS.DISABLED_FIELD.options.STATUS,
                              }) && (
                                <Button
                                  primary
                                  type="button"
                                  onClick={async () => {
                                    formik.setFieldValue('s_status', STATUS_LIST.RESOLVED.key);
                                    await sleep(1000);
                                    createOrUpdateTask(
                                      onResolveAndUpdateButtonClicked,
                                      { location, history },
                                      {
                                        ...formik,
                                        values: { ...(formik?.values || {}), s_status: STATUS_LIST.RESOLVED.key },
                                      }
                                    );
                                  }}
                                  disabled={buttonDisableState || loadingManifestForm}
                                >
                                  Resolve and Update
                                </Button>
                              )}
                            <Button
                              primary
                              type="button"
                              // disabled={canUpdateCheck({
                              //   initialData: {
                              //     initial: initialValues,
                              //     variableData: _.get(formik, 'values', {}),
                              //   },
                              // })}
                              onClick={() => {
                                createOrUpdateTask(onUpdate, { history, location }, formik);
                              }}
                              disabled={buttonDisableState || loadingManifestForm}
                            >
                              Update
                            </Button>
                            {generateMultiple && isTaskParent(task) && (
                              <Button
                                primary
                                type="button"
                                onClick={() => {
                                  handleModal(true);
                                }}
                              >
                                Generate Multiple
                              </Button>
                            )}
                          </ShouldComponentRender>
                        )}

                        {task.id && task.status === STATUS_LIST.DRAFT.key && false && (
                          <Button basic type="button" onClick={onDiscard}>
                            Discard
                          </Button>
                        )}

                        {!isActionRecordPresent && (
                          <Button basic type="button" onClick={onCancel}>
                            Cancel
                          </Button>
                        )}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </div>
              </form>
            );
          }}
        </Formik>
        {open && <GenerateMultipleModal handleModal={handleModal} open={open} task={task} />}
      </>
    );
  }
);

TaskForm.propTypes = {
  task: PropTypes.object.isRequired,
  onApprove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDiscard: PropTypes.func,
  initialValues: PropTypes.object.isRequired,
  validateForm: PropTypes.func.isRequired,
  onCreateButtonClicked: PropTypes.func.isRequired,
  buttonDisableState: PropTypes.bool,
  schema: PropTypes.object.isRequired,
  dependentTasks: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  linkedTasks: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  templateInfo: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  parentTaskTemplate: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  assetCriteriaTemplate: PropTypes.exact({
    isLoading: PropTypes.bool.isRequired,
    info: PropTypes.object,
  }).isRequired,
  showEquipmentColumnForParentTemplate: PropTypes.bool,
  parentTaskTemplateTaskTypesDict: PropTypes.object,
  taskTypeSelectedisParent: PropTypes.bool,
  onLinkTaskButtonClicked: PropTypes.func.isRequired,
  editLinkedTask: PropTypes.func.isRequired,
  onDeleteButtonClicked: PropTypes.func.isRequired,
  notificationWatchers: PropTypes.object,
  partsPicklist: PropTypes.object,
  manifestFormPreview: PropTypes.object,
  taskTypeOptions: PropTypes.object,
  loggedInUser: joyn2UserPropType,
  assetOptions: PropTypes.object,
  stopOptions: PropTypes.object,
  onParentTempalteSearchChange: PropTypes.func.isRequired,
  onParentTempalteItemChange: PropTypes.func.isRequired,
  onTemplateItemTaskTypeChanged: PropTypes.func,
  onAssetCriteriaChange: PropTypes.func,
  onAssetCriteriaSearchChange: PropTypes.func,
  allAreas: PropTypes.array,
  history: {
    goBack: PropTypes.func.isRequired,
  },
  taskHistory: PropTypes.array,
  taskEmailHistory: PropTypes.array,
  watchergroups: PropTypes.array,
  allTasks: PropTypes.array,
  onResolveAndUpdateButtonClicked: PropTypes.func.isRequired,
  vendorsDict: PropTypes.object.isRequired,
  evaluatedRules: PropTypes.array,
  settings: PropTypes.object,
  assetLevelsDict: PropTypes.object,
  ifSmallScreen: PropTypes.bool.isRequired,
};

export default TaskForm;
